import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { LogIn, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      const isAdmin = result.data.role === 'admin' || result.data.role === 'root_admin';
      navigate(isAdmin ? '/admin' : '/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12 bg-[#F9FAFB]" data-testid="login-page">
      <Card className="w-full max-w-md border border-zinc-200">
        <CardHeader className="text-center space-y-2">
          <div className="w-12 h-12 rounded-xl bg-[#7551e8] flex items-center justify-center mx-auto mb-2">
            <LogIn size={22} className="text-white" />
          </div>
          <CardTitle className="font-['Outfit'] text-2xl">Welcome Back</CardTitle>
          <CardDescription>Sign in to continue your preparation</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100" data-testid="login-error">{error}</div>}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)}
                required className="focus:ring-[#7551e8]" data-testid="login-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={showPw ? 'text' : 'password'} placeholder="Enter password" value={password}
                  onChange={e => setPassword(e.target.value)} required className="pr-10 focus:ring-[#7551e8]" data-testid="login-password" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#7551e8] hover:bg-[#5e3dc7] text-white py-5" data-testid="login-submit-btn">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <div className="text-center space-y-2">
              <Link to="/forgot-password" className="text-sm text-[#7551e8] hover:underline font-medium block" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
              <p className="text-sm text-[#52525B]">
                Don't have an account? <Link to="/register" className="text-[#7551e8] hover:underline font-medium" data-testid="goto-register">Sign up</Link>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
